import { useState, useEffect, useRef } from 'react';
import { useRallyMasterAlerts } from '@/hooks/useRallyMasterAlerts';
import axios from 'axios';
import { API } from '@/App';
import { useParams, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import DynamicTileLayer from '@/components/DynamicTileLayer';
import MapSelector from '@/components/MapSelector';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Users, RefreshCw, Clock, Tag, AlertTriangle, X } from 'lucide-react';
import { toast } from 'sonner';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Component to fit map to all markers
function FitBoundsToMarkers({ locations }) {
  const map = useMap();
  
  useEffect(() => {
    if (locations && locations.length > 0) {
      const bounds = L.latLngBounds(locations.map(loc => [loc.latitude, loc.longitude]));
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [locations, map]);
  
  return null;
}

// Generate consistent color based on user name
function stringToColor(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colors = [
    '#ef4444', '#f97316', '#f59e0b', '#84cc16', '#22c55e',
    '#14b8a6', '#06b6d4', '#3b82f6', '#6366f1', '#8b5cf6',
    '#a855f7', '#d946ef', '#ec4899', '#f43f5e'
  ];
  return colors[Math.abs(hash) % colors.length];
}

export default function LiveMapView({ user }) {
  const { eventId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [locations, setLocations] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [center, setCenter] = useState([40.4168, -3.7038]);
  const [showLabels, setShowLabels] = useState(true); // Toggle for name labels
  
  const refreshIntervalRef = useRef(null);
  const { recent: recentAlerts, dismiss: dismissAlert, stoppedUserIds } = useRallyMasterAlerts(event?.event_mode === 'free' ? null : eventId, event?.event_mode === 'free' ? [] : locations);

  useEffect(() => {
    fetchEventAndLocations();
    
    // Auto-refresh every 10 seconds
    if (autoRefresh) {
      refreshIntervalRef.current = setInterval(() => {
        fetchLocations();
      }, 10000);
    }
    
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [eventId, autoRefresh]);

  const fetchEventAndLocations = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Get event info
      const eventsResponse = await axios.get(`${API}/rally-master/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const eventData = eventsResponse.data.find(e => e.id === eventId);
      if (eventData) {
        setEvent(eventData);
      }
      
      // Get locations
      await fetchLocations();
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/rally-master/events/${eventId}/live-locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocations(response.data);
      setLastUpdate(new Date());
      
      // Update center if we have locations
      if (response.data.length > 0) {
        const avgLat = response.data.reduce((sum, loc) => sum + loc.latitude, 0) / response.data.length;
        const avgLng = response.data.reduce((sum, loc) => sum + loc.longitude, 0) / response.data.length;
        setCenter([avgLat, avgLng]);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const toggleAutoRefresh = () => {
    setAutoRefresh(!autoRefresh);
    if (!autoRefresh) {
      toast.success('Auto-actualización activada');
    } else {
      toast.info('Auto-actualización desactivada');
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    }
  };

  const formatLastUpdate = () => {
    if (!lastUpdate) return 'Nunca';
    return lastUpdate.toLocaleTimeString('es-ES');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 px-4 py-3">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/rally-master')}
              className="text-white bg-gray-700 hover:bg-gray-600 border-gray-600"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Volver
            </Button>
            <div>
              <h1 className="text-white font-semibold">{event?.title || 'Evento'}</h1>
              <p className="text-gray-400 text-sm">Mapa en Vivo - Participantes</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              <Users className="w-4 h-4" />
              <span>{locations.length} activos</span>
            </div>
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              <Clock className="w-4 h-4" />
              <span>Últ: {formatLastUpdate()}</span>
            </div>
            <Button
              onClick={() => setShowLabels(!showLabels)}
              variant={showLabels ? "default" : "outline"}
              size="sm"
              className={showLabels ? "bg-purple-600 hover:bg-purple-700" : ""}
              title="Mostrar/ocultar nombres"
            >
              <Tag className="w-4 h-4 mr-1" />
              Nombres
            </Button>
            <Button
              onClick={toggleAutoRefresh}
              variant={autoRefresh ? "default" : "outline"}
              size="sm"
              className={autoRefresh ? "bg-green-600 hover:bg-green-700" : ""}
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${autoRefresh ? 'animate-spin' : ''}`} />
              {autoRefresh ? 'Auto' : 'Manual'}
            </Button>
            <Button
              onClick={fetchLocations}
              variant="outline"
              size="sm"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="h-[calc(100vh-64px)] relative flex">
        {/* Visual alert banner (no sound) */}
        {recentAlerts.length > 0 && (
          <div className="absolute top-0 inset-x-0 z-[1000] p-2 space-y-1 pointer-events-none" data-testid="rm-alert-banner">
            {recentAlerts.slice(0, 3).map(a => (
              <div key={a.key + a.at} className={`pointer-events-auto flex items-center gap-3 rounded-lg border-2 px-4 py-3 shadow-2xl ${a.kind === 'stopped' ? 'bg-amber-500 border-amber-300 text-black' : a.kind === 'sos' ? 'bg-black border-red-500 text-red-400 animate-pulse' : 'bg-red-600 border-red-300 text-white animate-pulse'}`}>
                <AlertTriangle className="w-7 h-7 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-lg font-black uppercase tracking-wide leading-none">{a.kind === 'deviation' ? 'Desvío de ruta' : a.kind === 'speed' ? 'Exceso de velocidad' : a.kind === 'sos' ? 'SOS · Piloto pide ayuda' : 'Piloto parado +10 min'}</p>
                  <p className="text-sm font-semibold truncate">{a.name} · {new Date(a.at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</p>
                </div>
                <button onClick={() => dismissAlert(a.key)} className="p-2 rounded-md hover:bg-black/20" aria-label="Cerrar aviso" data-testid={`rm-alert-dismiss-${a.key}`}>
                  <X className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Participants sidebar */}
        {locations.length > 0 && (
          <div className="w-64 bg-gray-800 border-r border-gray-700 overflow-y-auto hidden md:block">
            <div className="p-3 border-b border-gray-700">
              <h3 className="text-white font-medium text-sm">Participantes Activos</h3>
            </div>
            {recentAlerts.length > 0 && (
              <div className="p-3 border-b border-gray-700 bg-red-900/30 space-y-1" data-testid="rm-recent-alerts">
                <p className="text-red-300 text-[11px] uppercase tracking-wider font-semibold flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Últimos avisos</p>
                {recentAlerts.slice(0, 4).map(a => (
                  <p key={a.key + a.at} className="text-xs text-red-100">
                    <span className="text-red-300">{new Date(a.at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</span> · {a.name}: {a.kind === 'deviation' ? 'desvío' : a.kind === 'speed' ? 'velocidad' : a.kind === 'sos' ? 'SOS' : 'parado +10 min'}
                  </p>
                ))}
              </div>
            )}
            <div className="divide-y divide-gray-700">
              {locations.map((loc) => (
                <div 
                  key={loc.user_id} 
                  className="p-3 hover:bg-gray-700/50 cursor-pointer transition-colors"
                  onClick={() => {
                    // Could add map centering on click later
                  }}
                >
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-4 h-4 rounded-full border-2 border-white flex-shrink-0"
                      style={{ backgroundColor: stringToColor(loc.user_name || loc.user_id) }}
                    />
                    {loc.dorsal && (
                      <span className="text-white font-bold text-lg">#{loc.dorsal}</span>
                    )}
                    <span className={`text-white ${loc.dorsal ? 'text-xs' : 'font-medium text-sm'} truncate`}>
                      {loc.user_name || 'Usuario'}
                    </span>
                    {stoppedUserIds.has(loc.user_id) && (
                      <span className="ml-auto px-1.5 py-0.5 rounded bg-red-600 text-white text-[10px] font-bold uppercase" data-testid={`stopped-badge-${loc.user_id}`}>Parado</span>
                    )}
                  </div>
                  <div className="ml-6 mt-1 text-xs text-gray-400 space-y-0.5">
                    <p>{loc.speed ? `${(loc.speed * 3.6).toFixed(0)} km/h` : 'Sin movimiento'}</p>
                    <p>
                      {new Date(loc.recorded_at || loc.updated_at).toLocaleTimeString('es-ES')}
                      {loc.source && loc.source !== 'app' && (
                        <span className="ml-1 px-1 rounded bg-orange-500/20 text-orange-300 uppercase text-[9px]" data-testid={`loc-source-${loc.user_id}`}>
                          {loc.source}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Map container */}
        <div className="flex-1 relative">
          {/* Map Style Selector */}
          <div className="absolute top-2 right-2 z-[1000]">
            <MapSelector compact />
          </div>
        
        <MapContainer
          center={center}
          zoom={12}
          style={{ height: '100%', width: '100%' }}
        >
          <DynamicTileLayer maxZoom={17} />
          
          {locations.map((loc, index) => (
            <CircleMarker
              key={loc.user_id}
              center={[loc.latitude, loc.longitude]}
              radius={12}
              fillColor={stringToColor(loc.user_name || loc.user_id)}
              fillOpacity={0.9}
              color="#ffffff"
              weight={3}
            >
              {/* Permanent name/dorsal label */}
              {showLabels && (
                <Tooltip 
                  permanent 
                  direction="top" 
                  offset={[0, -15]}
                  className="participant-label"
                >
                  <span style={{ 
                    fontWeight: 'bold', 
                    fontSize: loc.dorsal ? '14px' : '12px',
                    color: stringToColor(loc.user_name || loc.user_id),
                    textShadow: '1px 1px 2px white, -1px -1px 2px white, 1px -1px 2px white, -1px 1px 2px white',
                    backgroundColor: loc.dorsal ? 'rgba(255,255,255,0.9)' : 'transparent',
                    padding: loc.dorsal ? '2px 6px' : '0',
                    borderRadius: loc.dorsal ? '4px' : '0',
                    border: loc.dorsal ? `2px solid ${stringToColor(loc.user_name || loc.user_id)}` : 'none'
                  }}>
                    {loc.dorsal || loc.user_name || 'Usuario'}
                  </span>
                </Tooltip>
              )}
              <Popup>
                <div className="text-center min-w-[150px]">
                  {loc.dorsal && (
                    <p className="text-2xl font-bold mb-1" style={{ color: stringToColor(loc.user_name || loc.user_id) }}>
                      #{loc.dorsal}
                    </p>
                  )}
                  <p className={`font-bold ${loc.dorsal ? 'text-sm' : 'text-base'}`} style={{ color: loc.dorsal ? '#666' : stringToColor(loc.user_name || loc.user_id) }}>
                    {loc.user_name || 'Usuario'}
                  </p>
                  <hr className="my-2" />
                  <p className="text-sm text-gray-700">
                    {loc.speed ? `🏍️ ${(loc.speed * 3.6).toFixed(0)} km/h` : '🏍️ Velocidad N/A'}
                  </p>
                  {loc.altitude && (
                    <p className="text-sm text-gray-700">
                      ⛰️ {loc.altitude.toFixed(0)} m altitud
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    📍 {loc.latitude.toFixed(5)}, {loc.longitude.toFixed(5)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    🕐 {new Date(loc.updated_at).toLocaleTimeString('es-ES')}
                  </p>
                </div>
              </Popup>
            </CircleMarker>
          ))}
          
          {locations.length > 0 && <FitBoundsToMarkers locations={locations} />}
        </MapContainer>

        {/* No participants message */}
        {locations.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-[1000]">
            <Card className="mx-4 max-w-md">
              <CardContent className="py-8 px-8 text-center">
                <Users className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-semibold mb-2">Sin participantes activos</h3>
                <p className="text-gray-600 text-sm mb-4">
                  No hay participantes compartiendo su ubicación en los últimos 30 minutos.
                </p>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4 text-left">
                  <p className="text-yellow-800 text-xs font-medium mb-1">Para que los participantes aparezcan:</p>
                  <ol className="text-yellow-700 text-xs list-decimal list-inside space-y-1">
                    <li>Entrar en "Mis Inscripciones" → <strong>"Modo Baliza"</strong> y pulsar "Activar baliza", o</li>
                    <li>Tener <strong>Traccar Client</strong> encendido con su código personal, o</li>
                    <li>Tener vinculado su <strong>Trip Link de Spotwalla</strong> (posiciones cada 2 min)</li>
                  </ol>
                </div>
                <p className="text-gray-500 text-xs">
                  El mapa se actualiza automáticamente cada 10 segundos.
                </p>
                <Button
                  onClick={() => navigate('/rally-master')}
                  className="mt-6 bg-orange-500 hover:bg-orange-600"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Volver al Panel
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Mobile legend - only visible on small screens where sidebar is hidden */}
        <div className="absolute bottom-4 left-4 z-[1000] bg-white rounded-lg shadow-lg p-3 md:hidden">
          <h4 className="font-semibold text-xs mb-2">Participantes ({locations.length})</h4>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {locations.map(loc => (
              <div key={loc.user_id} className="flex items-center gap-2 text-xs">
                <div 
                  className="w-3 h-3 rounded-full flex-shrink-0" 
                  style={{ backgroundColor: stringToColor(loc.user_name || loc.user_id) }}
                ></div>
                <span className="truncate max-w-[120px]">{loc.user_name || 'Usuario'}</span>
              </div>
            ))}
            {locations.length === 0 && (
              <p className="text-gray-500 text-xs">Sin participantes</p>
            )}
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}
